'use client'

import { cn } from '@brew/ui/cn'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@brew/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@brew/ui/popover'
import { Check, ChevronDown, Store, X } from 'lucide-react'
import { useTranslations } from 'next-intl'
import {
  type KeyboardEvent,
  startTransition,
  useCallback,
  useRef,
  useState,
} from 'react'
import { IntentPrefetchLink } from '@/components/navigation/intent-prefetch-link'
import { useTemplateSearchQuery } from '@/lib/templates/search-query-state'
import { formatBrandNameFromDomain } from '../lib/brand-name'
import { useTemplatesBasePath } from '../lib/templates-surface-context'
import type { BrandWithCount } from '../lib/types'
import { buildTemplatesUrl } from '../lib/url-helpers'
import { TemplateBrandLogo } from './template-brand-logo'

interface BrandFilterComboboxProps {
  brands: Array<BrandWithCount>
  selectedBrand?: string
  currentType?: string
}

/**
 * Brand filter as the leading pill of the templates filter row: a
 * searchable combobox (Popover + cmdk) that replaced the old sidebar.
 * Selection is a URL param (`?brand=<domain>`, `?type` carried through),
 * opened through the prepared Link, like the category pills.
 *
 * Visual language mirrors the category pills exactly (`h-9 rounded-lg
 * border px-4 text-sm font-medium`, same selected/unselected recipes).
 * The clear "X" is a SIBLING of the popover trigger inside the pill span
 * (never nested — a button inside a button is invalid), following
 * `app/(app)/events/facet-combobox.tsx`.
 */
export function BrandFilterCombobox({
  brands,
  selectedBrand,
  currentType,
}: BrandFilterComboboxProps) {
  const t = useTranslations('templates')
  const basePath = useTemplatesBasePath()
  const [searchQuery] = useTemplateSearchQuery()
  const [isOpen, setIsOpen] = useState(false)
  const [activeValue, setActiveValue] = useState('')
  const isKeyboardSelectionRef = useRef(false)
  const commandRef = useRef<HTMLDivElement>(null)

  const hrefForBrand = useCallback(
    (brand: string | undefined) =>
      buildTemplatesUrl({
        type: currentType,
        brand,
        q: searchQuery,
        basePath,
      }),
    [currentType, basePath, searchQuery]
  )

  const handleCommandKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      isKeyboardSelectionRef.current = event.key === 'Enter'
    },
    []
  )
  const handleBrandClickCapture = useCallback(() => {
    isKeyboardSelectionRef.current = false
  }, [])
  const handleBrandSelect = useCallback(() => {
    // Partial Prefetching associates URL-specific content with the Link.
    // Enter must activate that same Link, rather than an imperative push.
    // Clear the marker before click re-enters cmdk's pointer selection.
    const isKeyboardSelection = isKeyboardSelectionRef.current
    isKeyboardSelectionRef.current = false
    if (isKeyboardSelection) {
      const activeLink = commandRef.current?.querySelector(
        'a[cmdk-item][data-selected="true"]'
      )
      if (activeLink instanceof HTMLAnchorElement) {
        activeLink.click()
      }
    }
    // Keep the prepared Link mounted until the router commits its transition.
    startTransition(() => setIsOpen(false))
  }, [])

  const selected = selectedBrand
    ? (brands.find((b) => b.brandDomain === selectedBrand) ?? {
        // Stale `?brand=` (URL survived a catalog change) — the pill must
        // never render empty, so derive display data from the domain.
        brandDomain: selectedBrand,
        brandName: formatBrandNameFromDomain(selectedBrand),
        count: 0,
      })
    : undefined

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <span
        className={cn(
          'flex h-8 shrink-0 animate-[fadeInUp_var(--duration-fast)_var(--ease-smooth-out)_both] items-center rounded-lg border font-medium text-xs transition-[color,background-color,border-color,opacity] motion-reduce:animate-none',
          {
            'border-border bg-white text-foreground dark:border-white/20 dark:bg-white/15':
              selected !== undefined,
            'border-border bg-muted/50 text-muted-foreground hover:bg-muted/80 hover:text-foreground/80 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10':
              selected === undefined,
          }
        )}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-expanded={isOpen}
            // Lead with the visible brand name when one is selected so
            // the accessible name contains the visible label (voice
            // control matches on it — WCAG 2.5.3).
            aria-label={
              selected
                ? t('brandFilter.labelSelected', { brand: selected.brandName })
                : t('brandFilter.label')
            }
            className={cn(
              'flex h-full cursor-pointer items-center rounded-lg pl-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60',
              {
                'pr-1': selected !== undefined,
                'pr-3': selected === undefined,
              }
            )}
          >
            {selected ? (
              <>
                <TemplateBrandLogo
                  domain={selected.brandDomain}
                  name={selected.brandName}
                  logoUrl={selected.logoUrl}
                  className="mr-1.5 size-3.5 shrink-0"
                />
                <span className="max-w-[120px] truncate whitespace-nowrap">
                  {selected.brandName}
                </span>
              </>
            ) : (
              <>
                <span className="mr-1.5 flex shrink-0 items-center border-border border-r pr-1.5">
                  <Store className="size-3 text-foreground/80" />
                </span>
                <span className="whitespace-nowrap">
                  {t('brandFilter.trigger')}
                </span>
                <ChevronDown
                  className={cn('ml-1 size-3 opacity-60 transition-transform', {
                    'rotate-180': isOpen,
                  })}
                />
              </>
            )}
          </button>
        </PopoverTrigger>
        {selected ? (
          <IntentPrefetchLink
            href={hrefForBrand(undefined)}
            isEager
            aria-label={t('brandFilter.clear')}
            className="mr-1 shrink-0 cursor-pointer rounded-lg p-0.5 transition-colors hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60 dark:hover:bg-white/10"
          >
            <X className="size-3" />
          </IntentPrefetchLink>
        ) : null}
      </span>
      {/* `overflow-hidden` keeps the inner Command clipped to this
          popover's `rounded-lg` border. */}
      <PopoverContent
        className="w-[280px] overflow-hidden rounded-lg p-0"
        align="start"
        collisionPadding={12}
      >
        <Command
          ref={commandRef}
          value={activeValue}
          onValueChange={setActiveValue}
          onKeyDownCapture={handleCommandKeyDownCapture}
        >
          <CommandInput placeholder={t('brandFilter.searchPlaceholder')} />
          <CommandList className="max-h-[320px] overflow-y-auto">
            <CommandEmpty className="py-4 text-center text-muted-foreground text-xs">
              {t('brandFilter.empty')}
            </CommandEmpty>
            <CommandGroup>
              {brands.map((brand) => {
                const isSelected = brand.brandDomain === selectedBrand
                return (
                  <CommandItem
                    asChild
                    key={brand.brandDomain}
                    // cmdk filters on this value — include the domain so
                    // "animoto.com" matches as well as "Animoto".
                    value={`${brand.brandName} ${brand.brandDomain}`}
                    onSelect={handleBrandSelect}
                    className="flex cursor-pointer items-center gap-2.5"
                  >
                    <IntentPrefetchLink
                      href={hrefForBrand(
                        isSelected ? undefined : brand.brandDomain
                      )}
                      // cmdk keeps keyboard focus on the input. Its active
                      // row is the keyboard intent, so prepare that row too.
                      isEager={
                        activeValue ===
                        `${brand.brandName} ${brand.brandDomain}`
                      }
                      // cmdk keeps focus on its input and announces the
                      // active option. A second tab stop can select a
                      // different option when Enter bubbles to Command.
                      tabIndex={-1}
                      onClickCapture={handleBrandClickCapture}
                    >
                      <Check
                        className={cn('size-3.5 shrink-0', {
                          'opacity-100': isSelected,
                          'opacity-0': !isSelected,
                        })}
                      />
                      <TemplateBrandLogo
                        domain={brand.brandDomain}
                        name={brand.brandName}
                        logoUrl={brand.logoUrl}
                        className="size-5 shrink-0"
                      />
                      <span className="min-w-0 flex-1 truncate text-sm">
                        {brand.brandName}
                      </span>
                      <span className="shrink-0 text-caption text-muted-foreground/60 tabular-nums">
                        {brand.count}
                      </span>
                    </IntentPrefetchLink>
                  </CommandItem>
                )
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
